// The buckets of the next action's date (R-U30, as «Мои сделки / задачи» of amoCRM): a date field grouped by when it is due; dropping a
// card into a bucket gives the record that bucket's date. Pure, so the board, the list and the tests share one rule. «Today» is the day
// of the account (`accountToday` of core/shared, its time zone setting), the same day the scenarios count by — not the browser's.

export type DueBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none';

/** The buckets in the order of the board; «На неделе» is the rest of the current calendar week (Monday to Sunday) after tomorrow, as in amoCRM (um/2026-11-03/01). */
export const DUE_BUCKETS: { key: DueBucket; label: string; color: string }[] = [
  { key: 'overdue', label: 'Просрочено', color: 'red' },
  { key: 'today', label: 'Сегодня', color: 'amber' },
  { key: 'tomorrow', label: 'Завтра', color: 'blue' },
  { key: 'week', label: 'На неделе', color: 'teal' },
  { key: 'later', label: 'Позже', color: 'violet' },
  { key: 'none', label: 'Без даты', color: 'gray' },
];

/** A calendar day (YYYY-MM-DD) shifted by whole days. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The Sunday that closes the calendar week (Monday to Sunday) of the day. */
export function weekEnd(day: string): string {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, (7 - dow) % 7);
}

/** The bucket of a date value; a datetime counts by its day, anything that is not a date has none. */
export function dueBucket(value: unknown, today: string): DueBucket {
  const day = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
  if (!day) return 'none';
  if (day < today) return 'overdue';
  if (day === today) return 'today';
  if (day === addDays(today, 1)) return 'tomorrow';
  if (day <= weekEnd(today)) return 'week';
  return 'later';
}

/**
 * The date a card dropped into the bucket gets: the first day of the bucket; «Без даты» clears it. «Просрочено» is no place to put
 * a card — a date in the past is not planned — so it gives nothing and the drop is refused; so is «На неделе» on Saturday and Sunday,
 * when the week has no day left after tomorrow.
 */
export function bucketDate(bucket: DueBucket, today: string): string | null | undefined {
  switch (bucket) {
    case 'today': return today;
    case 'tomorrow': return addDays(today, 1);
    case 'week': return addDays(today, 2) <= weekEnd(today) ? addDays(today, 2) : undefined;
    case 'later': { const after = addDays(weekEnd(today), 1), next = addDays(today, 2); return after > next ? after : next; }
    case 'none': return null;
    default: return undefined;
  }
}
